import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { X, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { selectUser } from '../../store/authSlice';

const defaultValues = {
  name: '',
  phone: '',
  email: '',
  type: 'Full-time',
  department: '',
  basic: 18000,
  hra: 4500,
  customAllowances: [{ label: '', amount: '' }],
  pf: true,
  esi: true,
  joined: '',
  pfOverrideRate: '',
  esiOverrideRate: '',
  esiOverrideThreshold: '',
};

export const EmployeeModal = ({ open, onOpenChange, onSave, employee, viewOnly = false }) => {
  const user = useSelector(selectUser);
  const businessConfig = user?.businessConfig || {};

  // Defaults from business config
  const defaultPfRate = businessConfig.pf?.employeeRate || 12;
  const defaultEsiRate = businessConfig.esi?.employeeRate || 0.75;
  const defaultEsiThreshold = businessConfig.esi?.grossThreshold || 21000;

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'customAllowances',
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  // Reset form when employee changes or modal opens
  useEffect(() => {
    if (employee) {
      const allowances = employee.customAllowances?.length
        ? employee.customAllowances
        : [{ label: '', amount: '' }];
      reset({
        ...employee,
        customAllowances: allowances,
        // Pre‑fill override fields if they exist
        pfOverrideRate: employee.pfOverride?.rate || '',
        esiOverrideRate: employee.esiOverride?.rate || '',
        esiOverrideThreshold: employee.esiOverride?.threshold || '',
      });
    } else {
      reset(defaultValues);
    }
  }, [employee, open, reset]);

  const onSubmit = (data) => {
    // Filter empty allowance rows
    const filteredAllowances = data.customAllowances.filter(
      (a) => a.label.trim() !== '' || Number(a.amount) > 0
    );

    // Build payload – include overrides only if filled
    const payload = {
      name: data.name,
      phone: data.phone,
      email: data.email,
      type: data.type,
      department: data.department,
      basic: Number(data.basic),
      hra: Number(data.hra),
      customAllowances: filteredAllowances,
      pf: data.pf,
      esi: data.esi,
      joined: data.joined,
    };

    // Override fields (optional)
    if (data.pfOverrideRate !== '') {
      payload.pfOverride = { rate: Number(data.pfOverrideRate) };
    }
    if (data.esiOverrideRate !== '' || data.esiOverrideThreshold !== '') {
      payload.esiOverride = {};
      if (data.esiOverrideRate !== '') payload.esiOverride.rate = Number(data.esiOverrideRate);
      if (data.esiOverrideThreshold !== '') payload.esiOverride.threshold = Number(data.esiOverrideThreshold);
    }

    onSave(payload);
  };

  // Compute gross for preview
  const watchedAllowances = watch('customAllowances') || [];
  const basic = Number(watch('basic')) || 0;
  const hra = Number(watch('hra')) || 0;
  const totalAllowances = watchedAllowances.reduce(
    (sum, a) => sum + (Number(a.amount) || 0),
    0
  );
  const gross = basic + hra + totalAllowances;

  // Determine which threshold to show for ESI (default or overridden)
  const esiThreshold = watch('esiOverrideThreshold') || defaultEsiThreshold;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {viewOnly ? 'Employee Profile' : employee ? 'Edit Employee' : 'Add Employee'}
          </DialogTitle>
          <DialogDescription>
            {viewOnly ? 'View employee details' : 'Fill in details and salary structure.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 pt-2">
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Full Name</Label>
              <Input {...register('name')} required disabled={viewOnly} data-testid="emp-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Phone</Label>
              <Input {...register('phone')} required disabled={viewOnly} data-testid="emp-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
              <Input type="email" {...register('email')} required disabled={viewOnly} data-testid="emp-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Employment Type</Label>
              <Select
                value={watch('type')}
                onValueChange={(v) => setValue('type', v)}
                disabled={viewOnly}
              >
                <SelectTrigger data-testid="emp-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Full-time">Full-time</SelectItem>
                  <SelectItem value="Part-time">Part-time</SelectItem>
                  <SelectItem value="Daily Wage">Daily Wage</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Department</Label>
              <Input {...register('department')} required disabled={viewOnly} data-testid="emp-department" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Date of Joining</Label>
              <Input type="date" {...register('joined')} required disabled={viewOnly} data-testid="emp-joined" />
            </div>
          </div>

          <Separator />

          {/* Salary Structure */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-600 font-medium mb-3">Salary Structure</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Basic Salary (₹)</Label>
                <Input
                  type="number"
                  {...register('basic', { valueAsNumber: true })}
                  required
                  disabled={viewOnly}
                  data-testid="emp-basic"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">HRA (₹)</Label>
                <Input
                  type="number"
                  {...register('hra', { valueAsNumber: true })}
                  required
                  disabled={viewOnly}
                  data-testid="emp-hra"
                />
              </div>
            </div>

            {/* Dynamic Custom Allowances */}
            <div className="mt-4 space-y-2">
              <Label className="text-xs text-slate-500">Custom Allowances</Label>
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-3">
                  <Input
                    {...register(`customAllowances.${index}.label`)}
                    placeholder="Label (e.g. Petrol)"
                    disabled={viewOnly}
                    className="flex-1"
                  />
                  <Input
                    {...register(`customAllowances.${index}.amount`, { valueAsNumber: true })}
                    placeholder="Amount"
                    type="number"
                    disabled={viewOnly}
                    className="w-32"
                  />
                  {!viewOnly && fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(index)}
                      className="h-8 w-8 p-0 text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              {!viewOnly && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ label: '', amount: '' })}
                  className="mt-2"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Allowance
                </Button>
              )}
            </div>

            {/* Gross Preview */}
            <div className="mt-3 p-3 bg-slate-50 rounded-md border border-slate-200 text-sm">
              <span className="font-medium text-slate-700">Gross Salary: </span>
              <span className="font-mono font-semibold text-slate-900">₹{gross.toLocaleString()}</span>
              <span className="text-xs text-slate-500 ml-2">(Basic + HRA + allowances)</span>
            </div>
          </div>

          <Separator />

          {/* PF & ESI – Dynamic */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-6 flex-wrap">
              <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-md bg-slate-50 border border-slate-200 flex-1 min-w-[200px]">
                <div>
                  <div className="text-sm font-medium text-slate-900">PF Applicable</div>
                  <div className="text-xs text-slate-500">
                    {defaultPfRate}% of Basic salary
                    {businessConfig.pf?.threshold && ` (basic > ₹${businessConfig.pf.threshold})`}
                  </div>
                </div>
                <Switch
                  checked={watch('pf')}
                  onCheckedChange={(v) => setValue('pf', v)}
                  disabled={viewOnly}
                  data-testid="emp-pf"
                />
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-md bg-slate-50 border border-slate-200 flex-1 min-w-[200px]">
                <div>
                  <div className="text-sm font-medium text-slate-900">ESI Applicable</div>
                  <div className="text-xs text-slate-500">
                    {defaultEsiRate}% of gross &nbsp;
                    {`(gross < ₹${esiThreshold})`}
                  </div>
                </div>
                <Switch
                  checked={watch('esi')}
                  onCheckedChange={(v) => setValue('esi', v)}
                  disabled={viewOnly}
                  data-testid="emp-esi"
                />
              </div>
            </div>

            {/* Advanced Overrides */}
            {!viewOnly && (
              <div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="text-slate-500 hover:text-slate-700"
                >
                  {showAdvanced ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                  {showAdvanced ? 'Hide' : 'Show'} advanced overrides
                </Button>
                {showAdvanced && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 p-4 border rounded-md bg-white">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-600">PF Rate (%) – override</Label>
                      <Input
                        {...register('pfOverrideRate')}
                        type="number"
                        placeholder={`Default: ${defaultPfRate}%`}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-600">ESI Rate (%) – override</Label>
                      <Input
                        {...register('esiOverrideRate')}
                        type="number"
                        placeholder={`Default: ${defaultEsiRate}%`}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs text-slate-600">ESI Threshold (₹) – override</Label>
                      <Input
                        {...register('esiOverrideThreshold')}
                        type="number"
                        placeholder={`Default: ₹${defaultEsiThreshold}`}
                        disabled={viewOnly}
                      />
                    </div>
                    <p className="text-xs text-slate-400 col-span-2">
                      Leave blank to use business defaults.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="emp-cancel">
              {viewOnly ? 'Close' : 'Cancel'}
            </Button>
            {!viewOnly && (
              <Button type="submit" className="bg-[#2E6DA4] hover:bg-[#255a88] text-white" data-testid="emp-save">
                {employee ? 'Save Changes' : 'Add Employee'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};